import { Download, EyeOff, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface NewProductsBulkToolbarProps {
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  onSelectAll: (checked: boolean) => void;
  onExport: () => void;
  onHide: () => void;
  onUnhide: () => void;
  onClearSelection: () => void;
  isExporting: boolean;
  isHiding: boolean;
  hasHiddenSelected: boolean;
}

export default function NewProductsBulkToolbar({
  selectedCount,
  totalCount,
  allSelected,
  onSelectAll,
  onExport,
  onHide,
  onUnhide,
  onClearSelection,
  isExporting,
  isHiding,
  hasHiddenSelected,
}: NewProductsBulkToolbarProps) {
  if (selectedCount === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/50 p-3">
      <div className="flex items-center gap-2">
        <Checkbox
          checked={allSelected}
          onCheckedChange={(checked) => onSelectAll(!!checked)}
        />
        <span className="text-sm font-medium">
          {selectedCount} van {totalCount} product(en) geselecteerd
        </span>
      </div>

      <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
        {/* Export Button */}
        <Button
          size="sm"
          variant="default"
          onClick={onExport}
          disabled={isExporting}
          className="bg-green-600 hover:bg-green-700"
        >
          <Download className="mr-2 h-4 w-4" />
          {isExporting ? "Exporteren..." : "Exporteer naar Excel"}
        </Button>

        {/* Hide/Unhide Button with Confirmation */}
        {hasHiddenSelected ? (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                disabled={isHiding}
              >
                <Eye className="mr-2 h-4 w-4" />
                Weer tonen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Producten weer tonen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Weet je zeker dat je {selectedCount} product(en) weer wilt tonen?
                  Deze worden weer zichtbaar in het overzicht.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <AlertDialogAction onClick={onUnhide}>
                  Ja, weer tonen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                disabled={isHiding}
                className="border-orange-300 text-orange-600 hover:bg-orange-50"
              >
                <EyeOff className="mr-2 h-4 w-4" />
                Niet meer tonen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Producten verbergen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Weet je zeker dat je {selectedCount} product(en) wilt verbergen?
                  Je kunt ze later terugvinden via "Toon verborgen producten".
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onHide}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  Ja, verbergen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Clear Selection */}
        <Button
          size="sm"
          variant="ghost"
          onClick={onClearSelection}
        >
          <X className="mr-2 h-4 w-4" />
          Deselecteren
        </Button>
      </div>
    </div>
  );
}
